import { format } from "date-fns";
import { useState, type MouseEvent, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VerticalSplitOutlinedIcon from "@mui/icons-material/VerticalSplitOutlined";
import { AllMattersV2Result } from "./AllMattersV2Result";
import { asRecord } from "./AllMattersV2Fields";
import { PropertyTypeTag, Tag, text } from "./AllMattersV2Parts";

import {
  isSignedUrlExpired,
  type V2DetailPdf,
  type V2DetailResult,
} from "../api/v2";
import { isHttpUrl } from "@/utils/link";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";

const statusMessages: Record<string, string> = {
  not_started: "解析がまだ完了していません。",
  failed: "解析に失敗しました。登記の PDF で内容を確認してください。",
};

/** Values of the selected detail row shown in the drawer header. */
export interface V2PanelProperty {
  prefecture?: string | null;
  location?: string | null;
  number?: string | null;
  type?: string | null;
}

/** Below this width the PDF and the result cannot both be read side by side. */
const SIDE_BY_SIDE_BREAKPOINT = "lg";

const nonEmpty = (...values: unknown[]): string =>
  values.find((v): v is string => typeof v === "string" && v !== "") ?? "";

const withoutQuery = (url: string): string => url.split(/[?#]/)[0];

/**
 * Every fetch re-signs the PDF URL. Swapping the iframe src would reload the
 * embedded PDF, so keep the loaded URL until it expires or points elsewhere.
 */
const useStablePdfUrl = (latest: string | null): string | null => {
  const [url, setUrl] = useState(latest);
  if (
    latest !== url &&
    (url == null ||
      latest == null ||
      withoutQuery(url) !== withoutQuery(latest) ||
      isSignedUrlExpired(url))
  ) {
    setUrl(latest);
  }
  return url;
};

const PanelHeader = ({
  property,
  result,
  sideBySide,
  onSideBySideChange,
  onClose,
}: {
  property: V2PanelProperty;
  result: Record<string, unknown> | null | undefined;
  /** Null hides the switch (narrow screens). */
  sideBySide: boolean | null;
  onSideBySideChange: (value: boolean) => void;
  onClose: () => void;
}): JSX.Element => {
  const type = nonEmpty(property.type, result?.種別);
  const prefecture = property.prefecture ?? "";
  const location = property.location ?? "";
  const address = location.startsWith(prefecture)
    ? location
    : `${prefecture}${location}`;
  const realEstateNumber = text(asRecord(result?.表題部).不動産番号);
  const numberLabel =
    type === "土地" ? "地番" : type ? "家屋番号" : "地番・家屋番号";
  const details = [
    property.number && `${numberLabel} ${property.number}`,
    realEstateNumber && `不動産番号 ${realEstateNumber}`,
  ].filter(Boolean);
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      sx={{ px: 2, pt: 1.5, pb: 1 }}
    >
      <Box id="allmatters-v2-panel-title" flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {type && <PropertyTypeTag type={type} />}
          {result?.閉鎖 === true && <Tag label="閉鎖" tone="error" filled />}
          <Typography
            component="h2"
            variant="h6"
            sx={{ overflowWrap: "anywhere", lineHeight: 1.4 }}
          >
            {nonEmpty(address, property.number) || "所在不明"}
          </Typography>
        </Stack>
        {details.length > 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.25,
              overflowWrap: "anywhere",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {details.join("　")}
          </Typography>
        )}
      </Box>
      {sideBySide != null && (
        <ToggleButton
          value="side-by-side"
          selected={sideBySide}
          onChange={() => {
            onSideBySideChange(!sideBySide);
          }}
          size="small"
          color="primary"
          sx={{
            minHeight: MIN_TAP_TARGET,
            px: 1.5,
            gap: 0.5,
            whiteSpace: "nowrap",
          }}
        >
          <VerticalSplitOutlinedIcon fontSize="small" />
          PDF と並べて表示
        </ToggleButton>
      )}
      <IconButton
        aria-label="詳細パネルを閉じる"
        onClick={onClose}
        sx={{ minHeight: MIN_TAP_TARGET, minWidth: MIN_TAP_TARGET }}
      >
        <CloseIcon />
      </IconButton>
    </Stack>
  );
};

export const AllMattersV2Panel = ({
  property,
  data,
  loading = false,
  error = false,
  sideBySide = false,
  onSideBySideChange,
  pdf,
  pdfLoading = false,
  pdfError = false,
  onClose,
  onRetry,
  onRetryPdf,
  actions,
}: {
  property: V2PanelProperty;
  data?: V2DetailResult;
  loading?: boolean;
  error?: boolean;
  /** From its own request, so the PDF shows without waiting for the result. */
  pdf?: V2DetailPdf;
  pdfLoading?: boolean;
  pdfError?: boolean;
  /** Show the PDF and the result next to each other on wide screens. */
  sideBySide?: boolean;
  /** Omit to hide the side-by-side switch. */
  onSideBySideChange?: (value: boolean) => void;
  onClose: () => void;
  onRetry: (() => void) | (() => Promise<V2DetailResult | undefined>);
  /** Re-signs the PDF URL; resolves with the fresh one. */
  onRetryPdf: (() => void) | (() => Promise<V2DetailPdf | undefined>);
  /** Extra links next to 再読み込み (e.g. to the request from the search page). */
  actions?: ReactNode;
}): JSX.Element => {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up(SIDE_BY_SIDE_BREAKPOINT));
  const canSplit = wide && onSideBySideChange != null;
  const split = canSplit && sideBySide;
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const latestPdfUrl = pdf?.pdfUrl && isHttpUrl(pdf.pdfUrl) ? pdf.pdfUrl : null;
  const pdfUrl = useStablePdfUrl(latestPdfUrl);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string>();
  const refresh = async (pdfOnly = false): Promise<void> => {
    setRefreshing(true);
    setRefreshError(undefined);
    try {
      await Promise.all(pdfOnly ? [onRetryPdf()] : [onRetry(), onRetryPdf()]);
    } catch {
      setRefreshError(
        "解析結果を取得できませんでした。時間をおいて再読み込みしてください。"
      );
    } finally {
      setRefreshing(false);
    }
  };

  const openPdf = async (
    event: MouseEvent<HTMLAnchorElement>
  ): Promise<void> => {
    const url = latestPdfUrl ?? pdfUrl;
    if (!url || !isSignedUrlExpired(url)) return;
    event.preventDefault();
    if (refreshing) return;
    // Reserve the browsing context during the user gesture, before awaiting SWR.
    const popup = window.open("about:blank", "_blank");
    if (!popup) {
      setRefreshError(
        "別タブを開けませんでした。ポップアップを許可してもう一度お試しください。"
      );
      return;
    }
    popup.opener = null;
    const policy = document.createElement("meta");
    policy.name = "referrer";
    policy.content = "no-referrer";
    popup.document.head.appendChild(policy);
    setRefreshing(true);
    setRefreshError(undefined);
    try {
      const fresh = await onRetryPdf();
      const freshUrl = fresh?.pdfUrl;
      if (!freshUrl || !isHttpUrl(freshUrl) || isSignedUrlExpired(freshUrl))
        throw new Error("PDF URL unavailable");
      if (!popup.closed) popup.location.replace(freshUrl);
    } catch {
      popup.close();
      setRefreshError(
        "PDF を開けませんでした。再読み込みしてもう一度お試しください。"
      );
    } finally {
      setRefreshing(false);
    }
  };
  const resultAvailable =
    data?.result != null && Object.keys(data.result).length > 0;

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "allmatters-v2-panel-title",
        sx: {
          width: split
            ? "96vw"
            : { xs: "100%", [MOBILE_BREAKPOINT]: "min(850px, 85vw)" },
          transition: theme.transitions.create("width"),
        },
      }}
    >
      <PanelHeader
        property={property}
        result={data?.result}
        sideBySide={canSplit ? split : null}
        onSideBySideChange={(value) => {
          onSideBySideChange?.(value);
        }}
        onClose={onClose}
      />
      <Box sx={{ px: 2, pb: 1 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <Button
            disabled={loading || refreshing}
            sx={{ minHeight: MIN_TAP_TARGET }}
            onClick={() => {
              void refresh();
            }}
          >
            再読み込み
          </Button>
          {actions}
        </Stack>
        {refreshError && <Alert severity="error">{refreshError}</Alert>}
        {error && data && (
          <Alert severity="warning">
            解析結果を更新できませんでした。前回取得した内容を表示しています。再読み込みしてください。
          </Alert>
        )}
      </Box>
      {/* Hidden, not reordered: the PDF iframe below must keep its place in the tree. */}
      <Tabs
        value={tab}
        onChange={(_, value: number) => {
          setTab(value);
        }}
        aria-label="登記の表示切り替え"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          px: 2,
          flexShrink: 0,
          ...(split && { display: "none" }),
        }}
      >
        <Tab
          id="allmatters-v2-pdf-tab"
          aria-controls="allmatters-v2-pdf-panel"
          label="PDF"
        />
        <Tab
          id="allmatters-v2-result-tab"
          aria-controls="allmatters-v2-result-panel"
          label="解析結果"
        />
      </Tabs>
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: split ? "row" : "column",
        }}
      >
        {/* Stays mounted while the result tab is open so the PDF never reloads. */}
        <Box
          id="allmatters-v2-pdf-panel"
          role={split ? "region" : "tabpanel"}
          aria-label={split ? "登記の PDF" : undefined}
          aria-labelledby={split ? undefined : "allmatters-v2-pdf-tab"}
          aria-hidden={!split && tab !== 0}
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            minHeight: 0,
            p: 2,
            display: "flex",
            flexDirection: "column",
            ...(split && { borderRight: 1, borderColor: "divider" }),
            ...(!split &&
              tab !== 0 && {
                position: "absolute",
                inset: 0,
                visibility: "hidden",
                pointerEvents: "none",
              }),
          }}
        >
          {pdfLoading ? (
            <Box role="status">
              <CircularProgress
                size={24}
                aria-label="登記の PDF を読み込み中"
              />
            </Box>
          ) : pdfError && !pdf ? (
            <Alert severity="error">
              登記の PDF を取得できませんでした。再読み込みしてください。
            </Alert>
          ) : pdfUrl ? (
            <Stack spacing={1} flex={1} minHeight={0}>
              <Button
                href={latestPdfUrl ?? pdfUrl}
                onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                  void openPdf(event);
                }}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: "flex-start", minHeight: 44 }}
              >
                PDF を別タブで開く
              </Button>
              <Box
                key={`${pdfUrl}:${page}`}
                component="iframe"
                title="登記の PDF"
                src={`${
                  pdfUrl.split("#")[0]
                }#page=${page}&view=FitH&navpanes=0`}
                sx={{
                  width: "100%",
                  flex: 1,
                  minHeight: "65vh",
                  boxSizing: "border-box",
                  border: 1,
                  borderColor: "divider",
                }}
              />
            </Stack>
          ) : (
            <Alert severity="info">表示できる登記の PDF がありません。</Alert>
          )}
        </Box>
        <Box
          id="allmatters-v2-result-panel"
          role={split ? "region" : "tabpanel"}
          aria-label={split ? "解析結果" : undefined}
          aria-labelledby={split ? undefined : "allmatters-v2-result-tab"}
          hidden={!split && tab !== 1}
          sx={{ overflowY: "auto", minHeight: 0, minWidth: 0, flex: "1 1 0" }}
        >
          {loading ? (
            <Box p={3} role="status">
              <CircularProgress size={24} aria-label="解析結果を読み込み中" />
            </Box>
          ) : error && !data ? (
            <Box p={2}>
              <Alert severity="error">
                解析結果を取得できませんでした。再読み込みしてください。
              </Alert>
            </Box>
          ) : resultAvailable && data?.result ? (
            <AllMattersV2Result
              result={data.result}
              notice={
                <>
                  {data.v2Status === "failed" && (
                    <Alert severity="warning">
                      最新の解析に失敗したため、前回成功した解析結果を表示しています。
                    </Alert>
                  )}
                  {data.resultAnalyzedAt && (
                    <Typography variant="body2" color="text.secondary">
                      表示中の結果の解析日時:{" "}
                      {format(
                        new Date(data.resultAnalyzedAt),
                        "yyyy/MM/dd HH:mm"
                      )}
                    </Typography>
                  )}
                </>
              }
              onShowPage={(nextPage) => {
                setPage(nextPage);
                // Side by side, the PDF is already visible on the left.
                if (!split) setTab(0);
                // A page jump reloads the iframe; an expired URL would fail.
                if (pdfUrl && isSignedUrlExpired(pdfUrl)) void refresh(true);
              }}
            />
          ) : (
            <Box p={2}>
              <Alert
                severity={data?.v2Status === "failed" ? "warning" : "info"}
              >
                {statusMessages[data?.v2Status ?? "not_started"] ??
                  "表示できる解析結果がありません。"}
              </Alert>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
